import React, { Dispatch, FC, SetStateAction, useEffect, useRef, useState, Fragment } from 'react';
import { Toggle } from '../forms/Toggle';
import { DropDown, SelectOptionProps } from '../forms/DropDown';
import { DefaultInput } from '../forms/DefaultInput';
import { DateInput } from '../forms/DateInput';
import { Address, Customer } from '../../../interfaces/Customer';
import styled from 'styled-components';
import {
  AdditionalQuestionnaireResultSet,
  Condition,
  Question,
  QuestionnaireWithResultSets,
} from '../../../interfaces/Questionnaire';
import { TextBox } from '../forms/TextBox';
import { getFullOffsetTop } from '../../../utils/ViewPosCalc';

const GridLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3.5rem 5rem;
  
  grid-column: span 2;
  
  margin-bottom: 5rem;
  
  & & {
    margin-bottom: 0;
  }
  
  & > * {
    padding-top: 0;
    
    & > label {
      height: 2.5rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
      
      & + div {
        min-height: 3rem;
      }
    }
  } 
`;

export interface QuestionnaireAnswers {
  [questionId: string]: string | number | boolean;
}

export interface QuestionnaireFormBuilderProps {
  answersState: [QuestionnaireAnswers, Dispatch<SetStateAction<QuestionnaireAnswers>>];
  customer?: Customer | null;
  address?: Address | null;
  questionnaireId?: string;
  questions?: Question[];
  showErrorStates?: boolean;
  onValueChanged?: (questionId: string, value: string | number | boolean, questionnaireId?: string) => void;
  onValuePreselected?: (questionId: string, value: string | number | boolean, questionnaireId?: string) => void;
}
export const QuestionnaireFormBuilder: FC<QuestionnaireFormBuilderProps> = ({ answersState, customer, address, onValueChanged, onValuePreselected, questionnaireId, questions = [], showErrorStates= false }) => {
  const [answers, setAnswers] = answersState;
  const lastEdited = useRef<string | null>(null);
  const [errorStates, setErrorStates] = useState<{ [ questionId: string ]: boolean }>({});
  const valueChangedTimeoutRef = useRef<any>(null);

  const handleErrorState = (id: string) => {
    const question = questions.find((question) => question.id === id);
    const value = answers[id];

    if (question) {
      const valid = validateQuestion(question, value);
      if (valid) {
        if (errorStates[id] === true) {
          const { [id]: item, ...nextErrorStates} = errorStates;
          setErrorStates(nextErrorStates);
        }
      } else {
        setErrorStates({
          ...errorStates,
          [id]: true,
        });
      }
      return valid;
    }
    return false;
  }

  const handleInput = (id: string) => ((e: React.ChangeEvent<any> | boolean | string) => {
    lastEdited.current = id;
    const value = typeof e === 'boolean' || typeof e === 'string' ? e : e.target.value;
    setAnswers({ ...answers, [id]: value });

    if (valueChangedTimeoutRef.current) {
      clearTimeout(valueChangedTimeoutRef.current);
      valueChangedTimeoutRef.current = null;
    }
    valueChangedTimeoutRef.current = setTimeout(() => {
      if (onValueChanged) {
        onValueChanged(id, value, questionnaireId);
      }
    }, 1000);

    // set error state
    handleErrorState(id);
  });

  useEffect(() => {
    if (showErrorStates) {
      let firstQuestionWithError: string | null = null;
      const questionsWithErrors = (questions || []).map((question) => {
        if (!handleErrorState(question.id)) {     // update error states
          if (firstQuestionWithError === null) {
            firstQuestionWithError = question.id;
          }
          return question.id;
        }
        return null;
      }).filter(Boolean);

      setErrorStates({
        ...errorStates,
        ...questionsWithErrors.reduce((result: any, id) => {
          if (id) {
            result[id] = true;
          }
          return result;
        }, {}),
      });

      if (firstQuestionWithError) {
        const view = document.querySelector(`#${firstQuestionWithError}`) as HTMLDivElement;
        if (view) {
          const offsetTop = getFullOffsetTop(view);
          document.querySelector('main.app')?.scrollTo({
            top: offsetTop,
            behavior: 'smooth',
          });
        }
      }
    } else {
      setErrorStates({});
    }
  }, [showErrorStates]);

  const getDefaultAnswerValue = (question: Question) => {
    switch (question.id) {
      // housing industry
      case 'HC-Q-Q1': return customer?.additionalAddresses?.length || 0;
      case 'HC-Q-Q9': return address?.type === 'Erdgas' && address?.consumption || 0;
      case 'HC-AQ-Q0': return address?.type === 'Erdgas' && address?.consumption || '';
      case 'HC-AQ-Q1': return address?.type === 'Wärme' && address?.consumption || '';
      case 'HC-AQ-Q2':
        switch (address?.supplier || '') {
          case 'DEW21': return 'DEW21';
          case 'mixed': return 'Gemischt';
          case 'extern': return 'Fremdversorgt';
          default: return '';
        }
      case 'HC-AQ-Q3': return address?.type || '';
      case 'HC-AQ-Q6': return !!address?.thermalLine || false;
      case 'HC-AQ-Q7': return Boolean(address?.type === 'Erdgas' && (address?.consumption || 0) > 400000);

      // major customers
      case 'MC-Q-Q1': return address?.type === 'Strom' && address?.consumption || 0;
      case 'MC-Q-Q2': return address?.type === 'Erdgas' && address?.consumption || 0;
      case 'MC-Q-Q12': return !!address?.thermalLine || false;

      // business customers
      case 'BC-Q-Q1': return address?.type === 'Strom' && address?.consumption || 0;
      case 'BC-Q-Q2': return address?.type === 'Erdgas' && address?.consumption || 0;
      case 'BC-Q-Q7': return !!address?.thermalLine || false;
      case 'BC-Q-Q8': return Boolean((address?.consumption || 0) < 80000);
    }

    switch (question.type || 'toggle') {
      case 'select':
        return question.options?.length > 0 && question.options[0]?.value || '';    // first selectable value
      case 'toggle':
        return true;    // default value
      default:
        return '';      // fallback
    }
  }

  useEffect(() => {
    if (questions.length > 0 && Object.keys(answers).length !== questions.length) {
      let nextAnswers = {};
      questions.map((question) => {
        nextAnswers = {
          ...nextAnswers,
          [question.id]: getDefaultAnswerValue(question),
        }
      });
      setAnswers(nextAnswers);
    }
  }, [questions]);

  useEffect(() => {
    questions.map((question) => {
      const conditions = question.conditions || [];
      const visible = conditions.length < 1 || conditions.some((condition) => (
        validateCondition(answers, condition)
      ));

      if (visible) {
        // handle conditional selection
        let answerPreselected = false;
        if (question.conditionalSelection?.length > 0) {
          question.conditionalSelection.map((conditionalSelection) => {
            const conditions = conditionalSelection.conditions || [];
            let allowConditionalSelection = false;
            const fulfilled = conditions.length < 1 || conditions.some((condition) => {
              // check if last edited
              const refIdLastEdited = (!lastEdited.current || lastEdited.current === condition.refId);
              if (refIdLastEdited) {
                allowConditionalSelection = true;
              }
              // validate
              return validateCondition(answers, condition);
            });
            if (fulfilled && allowConditionalSelection && answers[question.id] !== conditionalSelection.value) {
              setAnswers({
                ...answers,
                [question.id]: conditionalSelection.value,
              });
              answerPreselected = true;

              // preselection callback
              if (onValuePreselected) {
                onValuePreselected(question.id, conditionalSelection.value, questionnaireId);
              }
            }
          })
        }

        // default value preselection if field is visible
        if (!answerPreselected && answers[question.id] === '') {
          const defaultValue = getDefaultAnswerValue(question)
          if (defaultValue) {
            setAnswers({
              ...answers,
              [question.id]: getDefaultAnswerValue(question),
            });
          }
        }
      } else {
        // reset value to default
        if (typeof answers[question.id] === 'boolean' ? answers[question.id] !== true : answers[question.id]) {
          setAnswers({
            ...answers,
            [question.id]: typeof answers[question.id] === 'boolean' ? true : '',
          });
        }
      }
    });
  }, [answers]);

  return (
    <GridLayout>
      {
        questions.map((question) => {

          const conditions = question.conditions || [];
          const visible = conditions.length < 1 || conditions.some((condition) => (
            validateCondition(answers, condition)
          ));

          if (!visible) {
            return <Fragment key={question.id}/>;
          }

          switch (question.type || '') {
            case 'textarea':
              return (
                <TextBox
                  id={question.id}
                  key={question.id}
                  hint={question.hint}
                  label={question.label}
                  onChange={handleInput(question.id)}
                  value={answers[question.id] as string}
                  hasError={showErrorStates && errorStates[question.id] === true}/>
              );
            case 'date-yyyy':
            case 'date-yyyy-mm':
            case 'date-yyyy-mm-dd':
              return (
                <DateInput
                  id={question.id}
                  key={question.id}
                  label={question.label}
                  hint={question.hint}
                  type={(() => {
                    switch (question.type || '') {
                      case 'date-yyyy':
                        return 'year'
                      case 'date-yyyy-mm':
                        return 'month-year'
                      default:
                      case 'date-yyyy-mm-dd':
                        return 'day-month-year'
                    }
                  })()}
                  onChange={(value) => {
                    const escValue = typeof value === 'string' ? value : value.format('DD.MM.YYYY');
                    handleInput(question.id)(escValue);
                  }}
                  value={answers[question.id] as string}
                  hasError={showErrorStates && errorStates[question.id] === true}
                />
              );
            case 'toggle':
              return (
                <Toggle
                  id={question.id}
                  key={question.id}
                  label={question.label}
                  hint={question.hint}
                  onChange={handleInput(question.id)}
                  value={answers[question.id] as boolean}
                />
              );
            case 'select':
              return (
                <DropDown
                  id={question.id}
                  key={question.id}
                  label={question.label}
                  hint={question.hint}
                  options={(question.options || []) as SelectOptionProps[]}
                  onChange={({ target }) => {
                    const value = target.value;
                    const option = (question.options || []).find(({ key }) => key === value);
                    handleInput(question.id)(option?.value || value);
                  }}
                  value={(() => {
                    const value = answers[question.id] as string | number;
                    const option = (question.options || []).find((option) => option.value === value);
                    return option?.key || value;
                  })()}
                  hasError={showErrorStates && errorStates[question.id] === true}
                />
              );
            default:
            case 'number':
              return (
                <GridLayout key={question.id}>
                  <DefaultInput
                    id={question.id}
                    label={question.label}
                    hint={question.hint}
                    onChange={handleInput(question.id)}
                    value={answers[question.id] as string}
                    hasError={showErrorStates && errorStates[question.id] === true}
                  />
                </GridLayout>
              );
          }
        })
      }
    </GridLayout>
  )
}

export const validateCondition = (dataset: { [key: string]: string | number | boolean }, condition: Condition): boolean => {
  if (dataset.hasOwnProperty(condition.refId)) {
    let value = dataset[condition.refId];
    if (value === undefined) {
      if (Array.isArray(condition.value) && condition.value?.length > 0) {
        if (typeof condition.value[0] === 'number') {
          value = 0;
        } else if (typeof condition.value[0] === 'boolean') {
          value = true;
        } else {
          value = '';
        }
      } else if (typeof condition.value === 'number') {
        value = 0;
      } else if (typeof condition.value === 'boolean') {
        value = true;
      } else {
        value = '';
      }
    }

    const checkValue = (value: string | number | boolean, conditionValue: string | number | boolean) => {
      try {
        if (typeof conditionValue === 'number' && typeof value === 'string') {
          value = +value;     // parse number
        }
      } catch (err) {
        //
      }
      let result = false;
      switch (condition.type || 'isEqual') {
        case 'isLess':
          result = value < conditionValue;
          break;
        case 'isGreater':
          result = value >= conditionValue;
          break;
        default:
        case 'isEqual':
          result = value === conditionValue;
          break;
      }
      return result;
    }

    let result = false;
    if (Array.isArray(condition.value)) {
      result = condition.value.some((conditionValue) => checkValue(value, conditionValue));
    } else {
      result = checkValue(value, condition.value);
    }
    if (condition.conditions?.length > 0) {   // check sub conditions
      return result && condition.conditions.some((condition) => validateCondition(dataset, condition));
    }
    return result;
  }
  return false;
}

const validateQuestion = (question: Question, value: string | number | boolean) => {
  if (!question.required) {
    return true;
  }

  switch (question.type || 'number') {
    case 'date-yyyy':
      return  typeof value === 'string' && value.length === 4 && !Number.isNaN(+value);
    case 'date-yyyy-mm':
      return typeof value === 'string' && value.length === 7 && value.split('.').every((num) => !Number.isNaN(+num));
    case 'date-yyyy-mm-dd':
      return typeof value === 'string' && value.length === 10 && value.split('.').every((num) => !Number.isNaN(+num));
    case 'toggle':
    case 'select':
      return true;
    case 'number':
      return typeof value === 'string' && !Number.isNaN(+value) || typeof value === 'number' && value >= 0;
    case 'text':
    case 'textarea':
    default:
      return typeof value === 'string' && value.length > 0;
  }
}

export const validateQuestionnaire = (questionnaire: QuestionnaireWithResultSets, answers: QuestionnaireAnswers) => {
  const questions = (questionnaire?.questionnaire?.questions || [])
    .filter((question) => (
      (question.conditions || []).length < 1 || (question.conditions || []).some((condition) => validateCondition(answers, condition))
    ));
  return questions.length < 1 || questions.every((question) => {
    const value = answers[question.id];
    return validateQuestion(question, value);
  });
}

export const receiveResultSets = (questionnaire: QuestionnaireWithResultSets, answers: QuestionnaireAnswers): string[] => {
  const resultSets = (questionnaire?.resultSets || []).filter((resultSet) => {
    return (resultSet.questions?.length > 0 && resultSet.questions.every(({ id: refId, ...condition }) => (
      validateCondition(answers, { ...condition, refId })
    )));
  });
  const additionalResultSets = (questionnaire?.additionalResultSets || []).filter((resultSet) => {
    return (resultSet.questions?.length > 0 && resultSet.questions.every(({ id: refId, ...condition }) => (
      validateCondition(answers, { ...condition, refId })
    )));
  });

  const mergedResultSets = [];
  if (additionalResultSets?.length > 0) {
    const resultSetsIds = resultSets.map((item) => item.id);
    const resultSetsMatching = additionalResultSets.reduce((res: string[], item: AdditionalQuestionnaireResultSet) => {
      const matchingForItem = item.extends?.map((id) => {
        if (resultSetsIds.includes(id)) {
          return `${id}${item.id}`;
        }
        return null;
      }).filter(Boolean) as string[];

      return [...res, ...matchingForItem].filter((item, index, array) => array.indexOf(item) === index);
    }, []);
    mergedResultSets.push(...resultSetsMatching);
  } else {
    mergedResultSets.push(...resultSets.map(({ id }) => id));
  }

  return mergedResultSets;
}
